import Backbone from 'backbone'
import _ from 'lodash'
import bb from 'bounding-box'
import Arranger from './arranger'
import Resizer from './resizer'
import {Wob} from '../wob'
import $ from 'jquery'
import Wobs from '../wobs'

/*
 * A selection wob makes the outer bounding box of all selected wobs
 * a pseudo wob so it behaves like a wob. This allows us to resize it.
 *
 */
const SelectionWob = Wob.extend({

  id: -1,

  collection: new Backbone.Collection,

  selected: true,

  resizable: true,

  initialize: function (opts) {
    opts = opts || {}
    Wob.prototype.initialize.apply(this, arguments)
    this.selection = opts.selection
  },

  lockedAR: function () {
    return !!this.selection.wobs.filter(w => _.result(w, 'lockedAR')).length
  },

  resize: function (...args) {
    const [opts] = args.slice(-1) // Grab the opts passed to resize
    opts.silent = true // Don't trigger events on the wob
    Wob.prototype.resize.apply(this, args)

    // Trigger a change event so the view updates
    this.trigger('change', this, opts)
  }

})

const SelectionWidget = Backbone.Model.extend({
  initialize: function (opts) {
    let selection = opts.selection

    function minDimension (dimension, start, end) {
      let wob = _.maxBy(selection.wobs, wob => _.result(wob.widget.containerDefaults, 'min' + _.capitalize(dimension), 20))
        , wobMin = _.result(wob.widget.containerDefaults, 'min' + _.capitalize(dimension), 20)
        , bb = selection.boundingBox(selection.groupedWobs())
        , ratio = (bb[end] - bb[start]) / wob.get(dimension)

      return wobMin * ratio
    }

    // Implemented this weird way b/c the resizer asks for the minWidth using lodash _result, which binds `this`
    // to the containerDefaults. We need access to the `selection` so use a traditional closure.
    this.containerDefaults = {
      minWidth: minDimension.bind(null, 'width', 'left', 'right'),
      minHeight: minDimension.bind(null, 'height', 'top', 'bottom')
    }
  }
})

const Selection = Backbone.View.extend({

  className: 'wob-control selected multi-selection-control wob-0-degrees',

  initialize: function (opts) {
    this.wobs = []
    this.canvas = opts.canvas

    // Mock a model which we'll pass into the resizer to pretend we're a wob. This is used for resizing the entire selection
    this.model = new SelectionWob({widget: new SelectionWidget({selection: this}), selection: this})

    // We want to show the selection area as a resizable object, rather than show resize handles on each wob.
    this.resizer = new Resizer({wob: this.model, canvas: this.canvas, el: this.el})// Build a resize attached to this model
    this.listenTo(this.model, 'change', this.paint)
    this.listenTo(this.canvas.collection, 'move', this.updateModel)
    this.listenTo(this.canvas.collection, 'resize', this.wobResize)
    this.listenTo(this.resizer, 'end', this.endResize)
    this.listenTo(this.resizer, 'start', this.startResize)

    $(document).on('copy.canvas-selection', this.copy.bind(this))
    $(document).on('paste.canvas-selection', this.paste.bind(this))
  },

  // Note: This ignores connectors since connectors are not part of a selection
  boundingBox: function (wobs = []) {
    return wobs.reduce(function (p, c) {
      var dim = bb(c.area(), c.get('rotate'))
      return {
        left: Math.min(p.left, dim.left),
        right: Math.max(p.right, dim.right),
        top: Math.min(p.top, dim.top),
        bottom: Math.max(p.bottom, dim.bottom)
      }
    }, {
      left: Infinity,
      right: -Infinity,
      top: Infinity,
      bottom: -Infinity
    })
  },

  align: function (position) {
    return new Arranger(this.wobs, Wobs.filters.unlocked).align(position)
  },

  distribute: function (alignment) {
    return new Arranger(this.wobs, Wobs.filters.unlocked).distribute(alignment)
  },

  add: function (wob) {
    if (_.indexOf(this.wobs, wob) >= 0) { return }
    this.wobs.push(wob)
    wob.setSelected(true)
    return this
  },

  remove: function (wob) {
    if (_.indexOf(this.wobs, wob) < 0) { return }

    var index = this.wobs.indexOf(wob)
      , wobs = this.wobs.slice()
    wobs.splice(index, 1)
    this.wobs = wobs
    wob.setSelected(false)

    this.group()

    return this
  },

  clear: function () {
    var self = this
    this.wobs.forEach(function (wob) {
      self.remove(wob)
    })
  },

  moveBy: function (to) {
    this.wobs.filter(Wobs.filters.unlocked)
             .forEach(function (wob) {
               wob.moveBy(to)
             })

    this.updateModel()
  },

  /*
   * Fired before a grouped selection is resized. Triggers the `start:resize` event
   * on the wob DOM elements.
   */
  startResize: function () {
    this.wobs.forEach(wob => {
      let view = this.canvas.views[wob.cid]
      view.$el.trigger('start:resize') // trigger on the wob el
    })
  },

  /*
   * Fired after a grouped selection is resized. Triggers the `end:resize` event
   * on the wob DOM elements. The resizer does this for individual wob resizes, so
   * we need to make sure to do the same.
   */
  endResize: function () {
    this.wobs.forEach(wob => {
      let view = this.canvas.views[wob.cid]
      view.$el.trigger('end:resize') // trigger on the wob el
    })
    // Update the size in case any wobs decided to resize themself
    this.updateModel()
  },

  /*
   * This function will resize all the selected wobs to a new width/height.
   *
   * When the width value is set, it forces that width for all the wobs
   * If *just* the height value is set, it forces that height for all the wobs
   *
   * If both width and height are set, it will set that width and height for all wobs that don't have a locked
   * aspect ratio. For wobs that have a locked aspect ratio, we'll only set the width, so the height is ignored
   * for those wobs.
   */
  resize: function (width, height) {
    this.canvas.history.record()

    let origin = [0, 0] // This is the default for a wob resize. Resizes from the top/left corner

    this.wobs.filter(Wobs.filters.unlocked)
             .sort((a, b) => a.get('z-index') - b.get('z-index')) // maintain z-index order. See impact #38937
             .forEach(wob => {
               let angle = wob.get('rotate')

               if (angle) {
                 // the wob is rotated. We only support resizing the width OR the height. If both are set, abort
                 if (width && height) {
                   throw new Error('Rotated wobs can only be resized by width or by height')
                 }
                 if (width) {
                   origin = [0, .5] // Left middle side handle (lm)
                 }
                 if (height) {
                   origin = [.5, 0] // Top center handle (tc)
                 }

               }
               wob.resize(width, height, origin)
             })

    this.updateModel()
    this.canvas.resize()
  },

  // Fired when a wob is resized
  wobResize: function () {
    if (this.resizer.resizing) {
      // Ignore, the wob fired its `resize` event because we're adjust the entire group
      return
    }
    this.updateModel()
  },

  // I would call this trigger, but the backbone events has the trigger, and we're mixed in
  fire: function (evt) {
    this.wobs.forEach(function (wob) {
      wob.trigger(evt, _.tail(arguments))
    })
  },

  /*
   * Updates the pseudo selection with the new coords from the model (i.e. SelectionWob instance)
   */
  paint: function (model, opts) {
    if (opts.handle)  {
      // If there's a handle, they are resizing it, so we need to scale each inner wob
      this.wobs.filter(Wobs.filters.unlocked).forEach(wob => {
        let area = wob.area()
          , previous = _.clone(area)
          , ratio = { // Determine the coords as a percentage of the selection
            left: (area.left - model.previous('left')) / model.previous('width'),
            top: (area.top - model.previous('top')) / model.previous('height'),
            width: area.width / model.previous('width'),
            height: area.height / model.previous('height')
          }

        // reset the coords using using the updated selection coords
        area.left = model.get('left') + ratio.left * model.get('width')
        area.top = model.get('top') + ratio.top * model.get('height')
        area.width = ratio.width * model.get('width')
        area.height = ratio.height * model.get('height')

        wob.set(area)
        wob.trigger('resize', wob, previous)
      })
    }

    // Update the style of this selection
    let attrs = model.changed // Store so the closure knows how to paint it. Future updates could change the model `changed` props
    this.canvas.queue(() => {
      this.$el.css(attrs)
    })
  },

  /*
   * Updates the grouped model (if it's available, meaning this selection is grouped)
   * with the dimensions of all selected wobs
   */
  updateModel: function () {
    let bb = this.boundingBox(this.groupedWobs())

    this.model.set({
      left: bb.left,
      top: bb.top,
      width: bb.right - bb.left,
      height: bb.bottom - bb.top
    })
  },

  /*
   * Returns the wobs that should be a grouped selection, which are only
   * unlocked wobs. See https://github.com/SpiderStrategies/Scoreboard/issues/22725 for more details
   */
  groupedWobs: function () {
    if (this.wobs.length < 2) {
      // We're not grouping since there isn't at least two wobs selected
      return []
    }

    return this.wobs.filter(Wobs.filters.unlocked)
  },

  /*
   * Groups the selection into a single pseudo wob if the unlocked wob count > 1
   */
  group: function () {
    let wobs = this.groupedWobs()

    if (wobs.length > 1) {
      this.canvas.$el.addClass('multiple-selected')

      if (!$.contains(this.canvas.el, this.el)) {
        // The selection pseudo wob node isn't in the dom, so add it.
        this.canvas.$el.find('.wobs-controls')
                       .append(this.el)
        this.resizer.toggle(true)
      }
    } else {
      // Cleanup
      this.resizer.toggle(false)
      this.canvas.$el.removeClass('multiple-selected')
      this.$el.remove()
    }
    this.updateModel()
    return this
  },

  /*
   * Returns the bounding box of the view of the wob (widget). This is used to determine the
    * bounding box of the wob when it's rotated.
    *
    * If a wob view defines the `boundingBox` method, it is responsible for returning the
    * dimensions in *rotated* space!
    *
    * @param {Wob} wob - The wob to get the bounding box for
    * @returns {Object} - The bounding box of the wob
    */
  _getViewBb: function (wob) {
    const view = this.canvas.views[wob.cid]?.view
    const box = view?.boundingBox?.() || { left: 0, top: 0, width: 0, height: 0 }

    // Massage for different APIs. Canvas uses left/top, while the view uses x/y
    if (typeof box.x !== 'undefined') {
      box.left = box.x
      box.top = box.y

      delete box.x
      delete box.y
    }

    // Don't rotate the box, the view is responsible for returning the dimensions in rotated space
    return box
  },

  area: function (filter) {
    const getBBox = wob => {
      const bb = wob.boundingBox()
      const viewBb = this._getViewBb(wob)
      const left = wob.get('left') || 0
      const top = wob.get('top') || 0

      bb.left = Math.min(bb.left, left + viewBb.left)
      bb.right = Math.max(bb.right, left + viewBb.left + viewBb.width)
      bb.top = Math.min(bb.top, top + viewBb.top)
      bb.bottom = Math.max(bb.bottom, top + viewBb.top + viewBb.height)

      bb.width = bb.right - bb.left
      bb.height = bb.bottom - bb.top

      return bb
    }

    let wobs = filter ? this.wobs.filter(filter) : this.wobs

    if (wobs.length === 1) {
      const bb = getBBox(wobs[0])

      return bb
    }

    const area = {
      left: Number.POSITIVE_INFINITY,
      top: Number.POSITIVE_INFINITY,
      right: Number.NEGATIVE_INFINITY,
      bottom: Number.NEGATIVE_INFINITY
    }

    wobs.forEach(function (wob) {
      const sub = getBBox(wob)
      area.left = Math.min(area.left, sub.left)
      area.top = Math.min(area.top, sub.top)
      area.right = Math.max(area.right, sub.left + sub.width)
      area.bottom = Math.max(area.bottom, sub.top + sub.height)
    })

    area.width = area.right - area.left
    area.height = area.bottom - area.top

    return area
  },

  copy: function () {
    if (ignoreCopyPaste(this.canvas)) {
      return
    }

    // Create a copy of the select wob array in case different selections
    // are made before pasting
    this.copiedWobs = _.clone(this.wobs)
  },

  paste: function () {
    if (!this.canvas.duplicate) {
      return
    }

    if (ignoreCopyPaste(this.canvas)) {
      return
    }

    if (!this.copiedWobs || this.copiedWobs.length === 0) {
      return
    }

    // Create all the new copies in a single history "transaction", so that one
    // undo will remove all of them
    this.canvas.history.transaction(() => {
      let duplicatedWobs = this.canvas.duplicate(this.canvas, this.copiedWobs)

      let newWobs = duplicatedWobs.map(newWob => {
        // Add the new wob. Don't autoselect it we'll select them
        // all together later
        return this.canvas.create(newWob, null, { select: false })
      })

      // Select all the new wobs
      this.clear()
      newWobs.forEach(w => this.add(w))
      this.group()
    })
  },

  release: function () {
    this.wobs = []
    $(document).off('.canvas-selection')
  }
})

function ignoreCopyPaste (canvas) {
  if (focusedOnInput()) {
    return true
  }

  if (canvas.ignoreCopyPaste) {
    return canvas.ignoreCopyPaste()
  }

  return false
}

function focusedOnInput () {
  let activeElement = document.activeElement
  if (!activeElement) {
    return false
  }

  if (activeElement.nodeName === 'INPUT' || activeElement.nodeName === 'TEXTAREA') {
    return true
  }

  if ($(activeElement).closest('[contenteditable=true]').length > 0) {
    return true
  }

  return false
}

export default Selection
